import { Check, MoveRight, X } from 'lucide-react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import nodeWorkflowImg from '@/assets/workflows/node-workflow.png';
import simpleWorkflowImg from '@/assets/workflows/simple-workflow.png';
import { toast } from '@/lib/notify';

import { useCreateWorkflow } from '../api/useCreateWorkflow';

interface CreateWorkflowModalProps {
  open: boolean;
  onClose: () => void;
}

export function CreateWorkflowModal({ open, onClose }: CreateWorkflowModalProps) {
  const navigate = useNavigate();
  const { mutate, isPending } = useCreateWorkflow();

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  function handleChooseSimple() {
    mutate(
      { name: 'Untitled Workflow', templateType: 'Custom', type: 'KYC', structure: 'Simple' },
      {
        onSuccess: (data) => {
          onClose();
          navigate(`/workflows/${data.id}/simple`);
        },
        onError: () => {
          toast.error('Failed to create workflow');
        },
      },
    );
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/45" onClick={onClose} aria-hidden="true" />
      <div className="flex h-full justify-center p-5 lg:items-center">
        <div className="relative flex max-h-full w-full max-w-[728px] animate-modal flex-col gap-5 overflow-hidden rounded-3xl border border-placeholder bg-white p-5 shadow-[0px_0px_18.6px_0px_#0000001A] lg:gap-8 lg:p-7.5">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-base font-semibold text-black sm:text-lg sm:leading-5.5 lg:text-xl lg:leading-6">
              Choose workflow creation method
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#E5EDFF] text-primary transition hover:bg-[#d7e3ff]"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="h-full overflow-y-auto">
            <div className="grid gap-4 lg:grid-cols-2 lg:gap-5">
              {/* Simple Workflow */}
              <div className="space-y-4 rounded-[20px] border border-primary p-5 lg:space-y-4.5 lg:p-[15px] lg:pb-[25px]">
                <div className="w-full overflow-hidden rounded-xl">
                  <img
                    src={simpleWorkflowImg}
                    className="h-full w-full object-cover"
                    alt="Simple Workflow"
                  />
                </div>
                <div className="space-y-2">
                  <h3 className="text-base font-semibold text-black lg:text-lg lg:leading-5.5">
                    Simple Workflow
                  </h3>
                  <p className="text-xs leading-3.5 text-black/60">
                    Create a tailored automated process flow to handle and configure verification
                    session decisions.
                  </p>
                </div>
                <ul className="space-y-2">
                  <li className="flex items-start gap-1">
                    <Check className="mt-0.5 h-3 w-3 text-primary" />
                    <span className="text-[10px] leading-3">Easy to setup and configure</span>
                  </li>
                  <li className="flex items-start gap-1">
                    <Check className="mt-0.5 h-3 w-3 text-primary" />
                    <span className="text-[10px] leading-3">Linear verification flow</span>
                  </li>
                  <li className="flex items-start gap-1">
                    <Check className="mt-0.5 h-3 w-3 text-primary" />
                    <span className="text-[10px] leading-3">
                      Automatic status based on feature result
                    </span>
                  </li>
                </ul>
                <button
                  type="button"
                  onClick={handleChooseSimple}
                  disabled={isPending}
                  className="btn btn-primary w-full"
                >
                  <span>{isPending ? 'Creating…' : 'Choose'}</span>
                  {!isPending && <MoveRight className="h-4 w-4" />}
                </button>
              </div>

              {/* Node-Based Workflow */}
              <div className="space-y-4 rounded-[20px] border border-[#E4E4E4] p-5 lg:space-y-4.5 lg:p-[15px] lg:pb-[25px]">
                <div className="w-full overflow-hidden rounded-xl">
                  <img
                    src={nodeWorkflowImg}
                    className="h-full w-full object-cover"
                    alt="Node-Based Workflow"
                  />
                </div>
                <div className="space-y-2">
                  <h3 className="text-base font-semibold text-black lg:text-lg lg:leading-5.5">
                    Node-Based Workflow
                  </h3>
                  <p className="text-xs leading-3.5 text-black/60">
                    Advanced workflow with conditional branching, custom actions, and detailed
                    control.
                  </p>
                </div>
                <ul className="space-y-2">
                  <li className="flex items-start gap-1">
                    <Check className="mt-0.5 h-3 w-3" />
                    <span className="text-[10px] leading-3">
                      Conditional branching based on result
                    </span>
                  </li>
                  <li className="flex items-start gap-1">
                    <Check className="mt-0.5 h-3 w-3" />
                    <span className="text-[10px] leading-3">
                      Custom actions (tags, reviews, metadata)
                    </span>
                  </li>
                  <li className="flex items-start gap-1">
                    <Check className="mt-0.5 h-3 w-3" />
                    <span className="text-[10px] leading-3">Multiple verification paths</span>
                  </li>
                </ul>
                <button type="button" disabled className="btn btn-primary w-full opacity-50">
                  <span>Choose</span>
                  <MoveRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
